import { useEffect, useRef, useState } from 'react'
import useUsernameSearch from '../hooks/useUsernameSearch'

/* "Search By Client Name" — the reference's vue-multiselect box, same markup and
 * classes so styles/app.css styles it unchanged. One implementation for every
 * page that has the field (Account Statement, User History, User Win Loss,
 * Turnover, Total P/L, Sportbook, Live Casino, User Register Detail, User Lock).
 *
 * The flow, read off the reference (phone recording, 2026-09-21):
 *   1. tap the field       -> opens; the search box is EMPTY even when a name
 *                             is already picked; "List is empty."
 *   2. type 1-2 letters    -> "No elements found. Consider changing the search
 *                             query." — nothing is requested yet
 *   3. type the 3rd letter -> the downline is searched; matches list, first one
 *                             highlighted; none -> the same "No elements found…"
 *   4. delete back under 3 -> the list goes, "No elements found…" again;
 *                             empty box -> "List is empty."
 *   5. tap a name          -> closes, the field shows the name (onSelect(row))
 *   6. tap the SAME name   -> it is un-picked, the field goes back to the grey
 *                             placeholder (onChange('')) — vue-multiselect's
 *                             default for a second pick of the selected option
 *   7. tap outside, or the -> closes; what was typed is thrown away, the
 *      keyboard's Done (✓)    previous pick stays. Done only BLURS the input —
 *                             no tap lands anywhere — so the box closes on blur
 *                             (vue-multiselect: @blur -> deactivate), not only
 *                             on an outside tap. The outside listener stays for
 *                             iOS, where tapping dead space does not blur.
 *
 * So typing is only a SEARCH. The page's value changes on a pick or an un-pick,
 * never on a keystroke — a report cannot be loaded for a half-typed name, same
 * as the reference.
 *
 * The request rule (3-letter minimum, debounce, abort of the stale fetch) lives
 * in hooks/useUsernameSearch, shared with the header's Search User box.
 */

const PAGE_LIMIT = 15

function ClientMultiselect({ value, onChange, onSelect, placeholder = 'Select option' }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightIdx, setHighlightIdx] = useState(0)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  // true while a pointer that started INSIDE the box is still down — see onBlur
  const pointerInsideRef = useRef(false)

  const { results: suggestions, reset: resetSearch } = useUsernameSearch(open ? search : '', { limit: PAGE_LIMIT })

  // a fresh list always starts on its first row
  useEffect(() => { setHighlightIdx(0) }, [suggestions])

  // every open starts over, with an empty search box (step 1)
  const openBox = () => {
    if (open) return
    setSearch('')
    setHighlightIdx(0)
    setOpen(true)
  }

  // closing throws the typed text away; the picked value is the page's (step 7)
  const closeBox = () => {
    resetSearch()
    setSearch('')
    setHighlightIdx(0)
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closeBox()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
    // closeBox only touches setters and the hook's stable reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const pick = (row) => {
    if (!row) return
    // the selected option picked again = un-pick (step 6)
    if (value && row.username === value) onChange?.('')
    else onSelect?.(row)
    // a pick's blur must really close: the pointer is still down on the row
    pointerInsideRef.current = false
    closeBox()
    inputRef.current?.blur()
  }

  const onKeyDown = (e) => {
    // Enter never reaches a surrounding <form> — that would submit the page's
    // filter form (Load) while the operator is only choosing a name.
    if (e.key === 'Enter') {
      e.preventDefault()
      if (open) pick(suggestions[highlightIdx])
      return
    }
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((h) => (suggestions.length ? (h + 1) % suggestions.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((h) => (suggestions.length ? (h - 1 + suggestions.length) % suggestions.length : 0))
    } else if (e.key === 'Escape') {
      closeBox()
      inputRef.current?.blur()
    } else if (e.key === 'Tab') {
      closeBox()
    }
  }

  // Not tied to `open`: vue-multiselect v-shows these two rows off the search
  // text alone, so closed (empty search) the DOM still carries a visible
  // "List is empty." inside the hidden wrapper — as the reference captures do.
  //
  // And NOT tied to the request either. Hiding the row while a search was in
  // flight left the list with nothing in it, so the dropdown collapsed for the
  // ~300ms of the request and then jumped back open with the results — a
  // flicker on every 3rd letter (side-by-side recording, 2026-09-21). The
  // reference keeps "No elements found…" up until the matches replace it.
  const typed = search.trim()
  const showNoResult = typed.length > 0 && suggestions.length === 0
  const showEmpty = typed.length === 0 && suggestions.length === 0

  return (
    <div
      ref={rootRef}
      tabIndex="-1"
      role="combobox"
      aria-owns="listbox-null"
      className={`multiselect${open ? ' multiselect--active' : ''}`}
      onMouseDown={(e) => {
        // Anything inside the list keeps the input focused (vue-multiselect:
        // @mousedown.prevent on the wrapper) — otherwise dragging the list's
        // scrollbar would blur the input and the blur would close the box.
        // Option rows still get their own mousedown and pick.
        if (e.target.closest('.multiselect__content-wrapper')) { e.preventDefault(); return }
        if (e.target !== inputRef.current) {
          e.preventDefault()
          inputRef.current?.focus()
          openBox()
        }
      }}
      /* Touch: the closed box is a 0px-wide input behind a placeholder /
         picked-name span, so a tap lands on the span and the only way in is
         the handler above calling focus(). On iOS a programmatic focus() only
         sticks inside a real user gesture, and a touch's emulated mousedown
         fires ~300ms later, outside it — the first tap did nothing and the
         box needed 2-3 taps (user, 2026-09-22). touchstart IS the gesture:
         focus and open here; the later mousedown then finds it open and is a
         no-op. Not inside the list, where the row's own handlers pick. */
      onTouchStart={(e) => {
        pointerInsideRef.current = true
        if (e.target.closest('.multiselect__content-wrapper')) return
        if (open) return
        inputRef.current?.focus()
        openBox()
      }}
      onTouchEnd={() => { pointerInsideRef.current = false }}
      onTouchCancel={() => { pointerInsideRef.current = false }}
      onMouseDownCapture={() => { pointerInsideRef.current = true }}
      onMouseUp={() => { pointerInsideRef.current = false }}
    >
      <div className="multiselect__select"></div>
      {'  '}
      <div className="multiselect__tags">
        <div className="multiselect__tags-wrap" style={{ display: 'none' }}></div>
        {' '}
        <div className="multiselect__spinner" style={{ display: 'none' }}></div>
        {' '}
        <input
          ref={inputRef}
          name=""
          type="text"
          autoComplete="off"
          spellCheck="false"
          placeholder={placeholder}
          tabIndex="0"
          aria-controls="listbox-null"
          className="multiselect__input"
          value={search}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
          onFocus={openBox}
          /* The tap that OPENS the box also blurs it: focus() runs on
             touchstart / the root's mousedown, React re-renders the input
             from 0px to full width, and the same gesture's later
             mousedown / mouseup then lands on the re-laid-out element and
             moves focus for a moment → blur → closeBox → the box shut on its
             own tap. So the box took 2-3 taps to stay open (user, 2026-09-22,
             after the touchstart fix alone). A blur while a pointer that
             started inside the box is still down is the box's own gesture:
             put focus back and stay open. Real leaves (outside tap, Done key,
             Tab, Esc) never have a pointer down inside → close as before. */
          onBlur={() => {
            if (pointerInsideRef.current) { inputRef.current?.focus(); return }
            closeBox()
          }}
          onKeyDown={onKeyDown}
          style={open ? { width: '100%' } : { width: '0px', position: 'absolute', padding: '0px' }}
        />
        {' '}
        {!open && !value && (
          <span className="multiselect__placeholder">{`\n          ${placeholder}\n        `}</span>
        )}
        {!open && value ? <span className="multiselect__single">{value}</span> : null}
      </div>
      {' '}
      <div tabIndex="-1" className="multiselect__content-wrapper" style={{ maxHeight: '300px', display: open ? 'block' : 'none' }}>
        <ul role="listbox" id="listbox-null" className="multiselect__content" style={{ display: suggestions.length ? 'inline-block' : 'block' }}>
          {' '}
          {suggestions.map((u, i) => (
            <li
              key={u.wallet_id || u.user_id || u.staff_id || u.username}
              id={`null-${i}`}
              role="option"
              className="multiselect__element"
              onMouseDown={(e) => {
                e.preventDefault()
                pick(u)
              }}
            >
              <span
                className={`multiselect__option${i === highlightIdx ? ' multiselect__option--highlight' : ''}${value && u.username === value ? ' multiselect__option--selected' : ''}`}
                onMouseEnter={() => setHighlightIdx(i)}
              >{u.username}</span>
            </li>
          ))}
          {/* vue-multiselect keeps both status rows in the DOM (v-show) */}
          <li style={showNoResult ? {} : { display: 'none' }}><span className="multiselect__option">No elements found. Consider changing the search query.</span></li>
          {' '}
          <li style={showEmpty ? {} : { display: 'none' }}><span className="multiselect__option">List is empty.</span></li>
          {' '}
        </ul>
      </div>
    </div>
  )
}

export default ClientMultiselect
